/**
 * EntitlementProvider — single source of frontend "has this customer's
 * paid/trial access expired?" state (backend `entitlement.py` /
 * `entitlement_middleware.py` remain the actual authority; this just
 * mirrors it into React state so App.js can gate protected routes
 * without every page re-implementing the check).
 *
 * Two ways the status gets updated:
 *   1. A direct GET /billing/me poll on customer change + every 60s.
 *   2. Instantly on any API call returning 402 trial_expired (see the
 *      axios interceptor in lib/api.js) — so a trial that expires mid-
 *      session blocks access on the very next request, not just on the
 *      next poll tick.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { getCurrentCustomer, AUTH_EVENT } from "../lib/customerSession";

const EntitlementContext = createContext({ status: null, loading: true });
const POLL_MS = 60 * 1000;

export function EntitlementProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState(() => {
    try { const c = getCurrentCustomer(); return c?.id || c?.customer_id || null; } catch (_e) { return null; }
  });
  const cidRef = useRef(customerId);
  cidRef.current = customerId;

  const refresh = async () => {
    if (!cidRef.current) { setStatus(null); setLoading(false); return; }
    const res = await api.billingMe();
    if (res.ok) setStatus(res.data?.entitlement?.entitlement_status || null);
    setLoading(false);
  };

  useEffect(() => {
    const syncCustomer = () => {
      try { const c = getCurrentCustomer(); setCustomerId(c?.id || c?.customer_id || null); }
      catch (_e) { setCustomerId(null); }
    };
    window.addEventListener(AUTH_EVENT, syncCustomer);
    return () => window.removeEventListener(AUTH_EVENT, syncCustomer);
  }, []);

  useEffect(() => {
    setLoading(!!customerId);
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [customerId]); // eslint-disable-line

  useEffect(() => {
    const onExpired = () => setStatus("expired");
    window.addEventListener("core:entitlement-expired", onExpired);
    return () => window.removeEventListener("core:entitlement-expired", onExpired);
  }, []);

  return (
    <EntitlementContext.Provider value={{ status, loading }}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlement() {
  return useContext(EntitlementContext);
}
